import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition, IViewDefinition, IListSettings, IDataRow } from "../DBInterfaces";
import DBState from "../DBState";
import DBOutputSidebar from "../DBOutputSidebar";
import DBStringUtils from "../DBStringUtils";

export default class DBGeneratorPowerShell {

    // #region Public entry points

    public static ShowPowerShell = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCode: string = DBState.GetAppCode();
            const script: string = DBGeneratorPowerShell.GeneratePowerShellScript();
            const filename: string = appCode + "-CreateLists.ps1";
            DBOutputSidebar.ShowOutputSidebar("PowerShell Script", script, filename, "text/plain");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.ShowPowerShell");
        }
    };

    public static ExportPowerShell = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCode: string = DBState.GetAppCode();
            const script: string = DBGeneratorPowerShell.GeneratePowerShellScript();
            const filename: string = appCode + "-CreateLists.ps1";
            const blob: Blob = new Blob([script], { type: "text/plain" });
            const url: string = URL.createObjectURL(blob);
            const link: HTMLAnchorElement = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.ExportPowerShell");
        }
    };

    // #endregion

    // #region Script orchestrator

    public static GeneratePowerShellScript = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.GenerateScriptHeader();
            script += DBGeneratorPowerShell.GenerateConnectionPhase();
            script += DBGeneratorPowerShell.GenerateListCreationPhase();
            const IsCtEnabled = (list: IListDefinition): boolean => {
                return list.UseContentType === true;
            };
            const IsNotCtEnabled = (list: IListDefinition): boolean => {
                return list.UseContentType !== true;
            };
            const ctLists: IListDefinition[] = DBState.Lists.filter(IsCtEnabled);
            const nonCtLists: IListDefinition[] = DBState.Lists.filter(IsNotCtEnabled);
            if (ctLists.length > 0) {
                script += DBGeneratorPowerShell.GenerateSiteColumnsPhase(ctLists);
                script += DBGeneratorPowerShell.GenerateContentTypesPhase(ctLists);
            }
            script += DBGeneratorPowerShell.GenerateListColumnsPhase(nonCtLists);
            script += DBGeneratorPowerShell.GenerateLookupColumnsPhase(nonCtLists);
            script += DBGeneratorPowerShell.GenerateDataPhase();
            script += DBGeneratorPowerShell.GenerateViewsPhase();
            script += DBGeneratorPowerShell.GenerateCompletionMessage();
            script += DBGeneratorPowerShell.GenerateRemovalFunction();
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GeneratePowerShellScript");
            return "";
        }
    };

    // #endregion

    // #region Header and connection

    public static GenerateScriptHeader = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const stats = DBGeneratorPowerShell.CalculateStatistics();
            const appCode: string = DBState.GetAppCode();
            let header: string = "<#\n";
            header += ".SYNOPSIS\n";
            header += `    Creates SharePoint lists and libraries for the ${appCode} application.\n\n`;
            header += ".DESCRIPTION\n";
            header += `    This script creates ${stats.listCount} list(s) with ${stats.fieldCount} field(s), `;
            header += `${stats.dataRows} data row(s), and ${stats.viewCount} view(s).\n`;
            header += `    Estimated execution time: ~${stats.estimatedMinutes} minute(s).\n\n`;
            header += ".PARAMETER SiteUrl\n";
            header += "    The URL of the SharePoint site where lists will be created.\n\n";
            header += ".EXAMPLE\n";
            header += `    .\\${appCode}-CreateLists.ps1 -SiteUrl "https://tenant.sharepoint.com/sites/mysite"\n`;
            header += "#>\n\n";
            header += "[CmdletBinding()]\nparam(\n";
            header += '    [Parameter(Mandatory=$true)]\n';
            header += '    [string]$SiteUrl\n';
            header += ")\n\n";
            return header;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateScriptHeader");
            return "";
        }
    };

    public static CalculateStatistics = (): {
        listCount: number;
        fieldCount: number;
        dataRows: number;
        viewCount: number;
        ctCount: number;
        estimatedSeconds: number;
        estimatedMinutes: number;
    } => {
        if (ks.kErrorMode === true) { return { listCount: 0, fieldCount: 0, dataRows: 0, viewCount: 0, ctCount: 0, estimatedSeconds: 0, estimatedMinutes: 0 }; }
        try {
            const listCount: number = DBState.Lists.length;
            let fieldCount: number = 0;
            let dataRows: number = 0;
            let viewCount: number = 0;
            let ctCount: number = 0;
            const AccumulateStats = (list: IListDefinition): void => {
                fieldCount += Object.keys(list.Properties).length;
                if (list.Data !== null) { dataRows += list.Data.length; }
                if (list.Views !== null) { viewCount += list.Views.length; }
                if (list.UseContentType === true) { ctCount += 1; }
            };
            DBState.Lists.forEach(AccumulateStats);
            const estimatedSeconds: number = (listCount * 2) + (fieldCount * 1) + (dataRows * 0.5) + (viewCount * 1) + (ctCount * 3);
            const estimatedMinutes: number = Math.ceil(estimatedSeconds / 60);
            return { listCount, fieldCount, dataRows, viewCount, ctCount, estimatedSeconds, estimatedMinutes };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.CalculateStatistics");
            return { listCount: 0, fieldCount: 0, dataRows: 0, viewCount: 0, ctCount: 0, estimatedSeconds: 0, estimatedMinutes: 0 };
        }
    };

    public static GenerateConnectionPhase = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== CONNECTION =====\n\n";
            script += "# Check for PnP PowerShell module\n";
            script += 'if (-not (Get-Module -ListAvailable -Name "PnP.PowerShell")) {\n';
            script += '    Write-Error "PnP.PowerShell module is not installed. ';
            script += 'Install it with: Install-Module PnP.PowerShell -Scope CurrentUser"\n';
            script += "    return\n";
            script += "}\n\n";
            script += "# Connect to SharePoint\n";
            script += 'Write-Host "Connecting to $SiteUrl..." -ForegroundColor Cyan\n';
            script += "Connect-PnPOnline -Url $SiteUrl -Interactive\n\n";
            script += "# Verify connection\n";
            script += "$web = Get-PnPWeb\n";
            script += "if ($null -eq $web) {\n";
            script += '    Write-Error "Failed to connect to SharePoint site."\n';
            script += "    return\n";
            script += "}\n";
            script += 'Write-Host "Connected to: $($web.Title)" -ForegroundColor Green\n\n';
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateConnectionPhase");
            return "";
        }
    };

    // #endregion

    // #region List creation

    public static GenerateListCreationPhase = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== LIST CREATION =====\n\n";
            const AppendListCreation = (list: IListDefinition): void => {
                script += DBGeneratorPowerShell.GenerateSingleListCreation(list);
            };
            DBState.Lists.forEach(AppendListCreation);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateListCreationPhase");
            return "";
        }
    };

    public static GenerateSingleListCreation = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const templateName: string = DBGeneratorPowerShell.GetTemplateName(list.ListTemplate);
            const typeLabel: string = list.ListType === "library" ? "library" : "list";
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            let script: string = `# Create ${typeLabel}: ${list.Title}\n`;
            script += `$existingList = Get-PnPList -Identity "${escapedTitle}" -ErrorAction SilentlyContinue\n`;
            script += "if ($null -eq $existingList) {\n";
            script += `    New-PnPList -Title "${escapedTitle}" -Template ${templateName}\n`;
            script += `    Write-Host "Created ${typeLabel}: ${escapedTitle}" -ForegroundColor Green\n`;
            script += "} else {\n";
            script += `    Write-Host "${typeLabel} already exists: ${escapedTitle}" -ForegroundColor Yellow\n`;
            script += "}\n\n";
            script += DBGeneratorPowerShell.GenerateListSettings(list);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSingleListCreation");
            return "";
        }
    };

    public static GetTemplateName = (listTemplate: number): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (listTemplate === 101) { return "DocumentLibrary"; }
            if (listTemplate === 109) { return "PictureLibrary"; }
            return "GenericList";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GetTemplateName");
            return "";
        }
    };

    public static GenerateListSettings = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const settings: IListSettings | null = list.Settings;
            if (settings === null) { return ""; }
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            let script: string = "";
            let params: string = "";
            params += DBGeneratorPowerShell.BuildSettingsParams(settings);
            if (params.length > 0) {
                script += `Set-PnPList -Identity "${escapedTitle}"${params}\n\n`;
            }
            script += DBGeneratorPowerShell.GenerateSecuritySettings(list, settings);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateListSettings");
            return "";
        }
    };

    public static BuildSettingsParams = (settings: IListSettings): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let params: string = "";
            if (settings.EnableVersioning === true) {
                params += " -EnableVersioning $true";
            }
            if (settings.MajorVersionLimit !== null && settings.MajorVersionLimit !== undefined) {
                params += ` -MajorVersions ${settings.MajorVersionLimit}`;
            }
            if (settings.EnableMinorVersions === true) {
                params += " -EnableMinorVersions $true";
            }
            if (settings.EnableModeration === true) {
                params += " -EnableModeration $true";
            }
            if (settings.EnableAttachments === false) {
                params += " -EnableAttachments $false";
            }
            if (settings.EnableFolderCreation === true) {
                params += " -EnableFolderCreation $true";
            }
            if (settings.ContentTypesEnabled === true) {
                params += " -EnableContentTypes $true";
            }
            if (settings.DisableGridEditing === true) {
                params += " -DisableGridEditing $true";
            }
            if (settings.ForceCheckout === true) {
                params += " -ForceCheckout $true";
            }
            if (settings.Hidden === true) {
                params += " -Hidden $true";
            }
            return params;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.BuildSettingsParams");
            return "";
        }
    };

    public static GenerateSecuritySettings = (list: IListDefinition, settings: IListSettings): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            if (settings.ReadSecurity !== null && settings.ReadSecurity !== undefined && settings.ReadSecurity !== 1) {
                script += `# Set read security for ${list.Title}\n`;
                script += `$list = Get-PnPList -Identity "${escapedTitle}"\n`;
                script += `$list.ReadSecurity = ${settings.ReadSecurity}\n`;
                script += "$list.Update()\n";
                script += "Invoke-PnPQuery\n\n";
            }
            if (settings.WriteSecurity !== null && settings.WriteSecurity !== undefined && settings.WriteSecurity !== 1) {
                script += `# Set write security for ${list.Title}\n`;
                script += `$list = Get-PnPList -Identity "${escapedTitle}"\n`;
                script += `$list.WriteSecurity = ${settings.WriteSecurity}\n`;
                script += "$list.Update()\n";
                script += "Invoke-PnPQuery\n\n";
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSecuritySettings");
            return "";
        }
    };

    // #endregion

    // #region Site columns and content types

    public static GenerateSiteColumnsPhase = (ctLists: IListDefinition[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== SITE COLUMNS =====\n\n";
            const AppendSiteColumns = (list: IListDefinition): void => {
                script += DBGeneratorPowerShell.GenerateSiteColumnsForList(list);
            };
            ctLists.forEach(AppendSiteColumns);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSiteColumnsPhase");
            return "";
        }
    };

    public static GenerateSiteColumnsForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const fieldNames: string[] = Object.keys(list.Properties);
            const group: string = list.ContentTypeGroup !== null && list.ContentTypeGroup !== undefined
                ? list.ContentTypeGroup : DBState.GetAppCode();
            const AppendNonLookupField = (fieldName: string): void => {
                const field: IFieldDefinition = list.Properties[fieldName];
                if (field.Type === "lookup") { return; }
                script += DBGeneratorPowerShell.GenerateFieldPowerShell(list.Title, fieldName, field, group);
            };
            const AppendLookupField = (fieldName: string): void => {
                const field: IFieldDefinition = list.Properties[fieldName];
                if (field.Type !== "lookup") { return; }
                script += DBGeneratorPowerShell.GenerateFieldPowerShell(list.Title, fieldName, field, group);
            };
            fieldNames.forEach(AppendNonLookupField);
            fieldNames.forEach(AppendLookupField);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSiteColumnsForList");
            return "";
        }
    };

    public static GenerateContentTypesPhase = (ctLists: IListDefinition[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== CONTENT TYPES =====\n\n";
            const AppendContentType = (list: IListDefinition): void => {
                script += DBGeneratorPowerShell.GenerateContentTypeForList(list);
            };
            ctLists.forEach(AppendContentType);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateContentTypesPhase");
            return "";
        }
    };

    public static GenerateContentTypeForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ctName: string = DBStringUtils.GetContentTypeName(list);
            const group: string = list.ContentTypeGroup !== null && list.ContentTypeGroup !== undefined
                ? list.ContentTypeGroup : DBState.GetAppCode();
            const parentCt: string = list.ListType === "library" ? "Document" : "Item";
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            let script: string = `# Content type for ${list.Title}\n`;
            script += `$parentCT = Get-PnPContentType -Identity "${parentCt}"\n`;
            script += `Add-PnPContentType -Name "${ctName}" -Group "${group}" -ParentContentType $parentCT\n`;
            script += DBGeneratorPowerShell.GenerateContentTypeFieldLinks(list, ctName);
            script += `Add-PnPContentTypeToList -List "${escapedTitle}" -ContentType "${ctName}"\n`;
            script += `Write-Host "Added content type ${ctName} to ${escapedTitle}" -ForegroundColor Green\n\n`;
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateContentTypeForList");
            return "";
        }
    };

    public static GenerateContentTypeFieldLinks = (list: IListDefinition, ctName: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const fieldNames: string[] = Object.keys(list.Properties);
            const AppendFieldLink = (fieldName: string): void => {
                const field: IFieldDefinition = list.Properties[fieldName];
                const required: string = field.Required === true ? " -Required" : "";
                script += `Add-PnPFieldToContentType -Field "${fieldName}" -ContentType "${ctName}"${required}\n`;
            };
            fieldNames.forEach(AppendFieldLink);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateContentTypeFieldLinks");
            return "";
        }
    };

    // #endregion

    // #region List columns (non-CT)

    public static GenerateListColumnsPhase = (nonCtLists: IListDefinition[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== LIST COLUMNS =====\n\n";
            const AppendListColumns = (list: IListDefinition): void => {
                script += DBGeneratorPowerShell.GenerateNonLookupColumnsForList(list);
            };
            nonCtLists.forEach(AppendListColumns);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateListColumnsPhase");
            return "";
        }
    };

    public static GenerateNonLookupColumnsForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const fieldNames: string[] = Object.keys(list.Properties);
            const AppendNonLookupField = (fieldName: string): void => {
                const field: IFieldDefinition = list.Properties[fieldName];
                if (field.Type === "lookup") { return; }
                script += DBGeneratorPowerShell.GenerateFieldPowerShell(list.Title, fieldName, field);
            };
            fieldNames.forEach(AppendNonLookupField);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateNonLookupColumnsForList");
            return "";
        }
    };

    public static GenerateLookupColumnsPhase = (nonCtLists: IListDefinition[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== LOOKUP COLUMNS =====\n\n";
            const AppendLookupColumns = (list: IListDefinition): void => {
                script += DBGeneratorPowerShell.GenerateLookupColumnsForList(list);
            };
            nonCtLists.forEach(AppendLookupColumns);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateLookupColumnsPhase");
            return "";
        }
    };

    public static GenerateLookupColumnsForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const fieldNames: string[] = Object.keys(list.Properties);
            const AppendLookupField = (fieldName: string): void => {
                const field: IFieldDefinition = list.Properties[fieldName];
                if (field.Type !== "lookup") { return; }
                script += DBGeneratorPowerShell.GenerateFieldPowerShell(list.Title, fieldName, field);
            };
            fieldNames.forEach(AppendLookupField);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateLookupColumnsForList");
            return "";
        }
    };

    // #endregion

    // #region Data phase

    public static GenerateDataPhase = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== DATA =====\n\n";
            const AppendListData = (list: IListDefinition): void => {
                if (list.Data === null) { return; }
                if (list.Data.length === 0) { return; }
                script += DBGeneratorPowerShell.GenerateDataForList(list);
            };
            DBState.Lists.forEach(AppendListData);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateDataPhase");
            return "";
        }
    };

    public static GenerateDataForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            let script: string = `# Data for ${list.Title}\n`;
            script += `Write-Host "Adding data to ${escapedTitle}..." -ForegroundColor Cyan\n`;
            let itemIndex: number = 0;
            const AppendDataRow = (row: IDataRow): void => {
                script += DBGeneratorPowerShell.GenerateSingleDataRow(list, row, itemIndex);
                itemIndex += 1;
            };
            list.Data.forEach(AppendDataRow);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateDataForList");
            return "";
        }
    };

    public static GenerateSingleDataRow = (list: IListDefinition, row: IDataRow, index: number): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            const varName: string = "$item" + index;
            let script: string = `${varName} = @{\n`;
            const keys: string[] = Object.keys(row);
            const AppendField = (key: string): void => {
                const value: string = row[key];
                const formattedValue: string = DBGeneratorPowerShell.FormatDataValue(list, key, value);
                script += `    "${key}" = ${formattedValue}\n`;
            };
            keys.forEach(AppendField);
            script += "}\n";
            script += `Add-PnPListItem -List "${escapedTitle}" -Values ${varName}\n`;
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSingleDataRow");
            return "";
        }
    };

    public static FormatDataValue = (list: IListDefinition, key: string, value: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const strValue: string = String(value);
            const field: IFieldDefinition | undefined = list.Properties[key];
            if (field === null || field === undefined) {
                return '"' + DBStringUtils.EscapePowerShellString(strValue) + '"';
            }
            if (field.Type === "lookup") {
                return "# TODO: Resolve lookup value for " + key;
            }
            if (field.Type === "boolean") {
                const lower: string = strValue.toLowerCase();
                if (lower === "true" || lower === "yes" || lower === "1") {
                    return "$true";
                }
                return "$false";
            }
            if (field.Type === "integer" || field.Type === "number") {
                return strValue;
            }
            return '"' + DBStringUtils.EscapePowerShellString(strValue) + '"';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.FormatDataValue");
            return "";
        }
    };

    // #endregion

    // #region Views phase

    public static GenerateViewsPhase = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== VIEWS =====\n\n";
            const AppendListViews = (list: IListDefinition): void => {
                if (list.Views === null) { return; }
                if (list.Views.length === 0) { return; }
                script += DBGeneratorPowerShell.GenerateViewsForList(list);
            };
            DBState.Lists.forEach(AppendListViews);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateViewsPhase");
            return "";
        }
    };

    public static GenerateViewsForList = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            let script: string = `# Views for ${list.Title}\n`;
            script += `Write-Host "Creating views for ${escapedTitle}..." -ForegroundColor Cyan\n`;
            const AppendView = (view: IViewDefinition): void => {
                script += DBGeneratorPowerShell.GenerateSingleView(list, view);
            };
            list.Views.forEach(AppendView);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateViewsForList");
            return "";
        }
    };

    public static GenerateSingleView = (list: IListDefinition, view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const escapedListTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            const escapedViewTitle: string = DBStringUtils.EscapePowerShellString(view.Title);
            let script: string = `# View: ${view.Title}\n`;
            const fieldsArray: string = DBGeneratorPowerShell.BuildViewFieldsArray(view);
            script += `$viewFields = @(${fieldsArray})\n`;
            let params: string = ` -List "${escapedListTitle}" -Title "${escapedViewTitle}" -Fields $viewFields`;
            if (view.ViewQuery !== null && view.ViewQuery !== undefined && view.ViewQuery.length > 0) {
                params += ` -Query '${view.ViewQuery.replace(/'/g, "''")}'`;
            }
            if (view.Paged === true) { params += " -Paged"; }
            if (view.IsDefault === true) { params += " -SetAsDefault"; }
            if (view.RowLimit !== null && view.RowLimit !== undefined) {
                params += ` -RowLimit ${view.RowLimit}`;
            }
            script += `Add-PnPView${params}\n`;
            script += DBGeneratorPowerShell.GenerateViewPostSettings(list, view);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateSingleView");
            return "";
        }
    };

    public static BuildViewFieldsArray = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let fields: string = "";
            const AppendField = (fieldName: string): void => {
                if (fields.length > 0) { fields += ", "; }
                fields += '"' + fieldName + '"';
            };
            view.ViewFields.forEach(AppendField);
            return fields;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.BuildViewFieldsArray");
            return "";
        }
    };

    public static GenerateViewPostSettings = (list: IListDefinition, view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const escapedListTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
            const escapedViewTitle: string = DBStringUtils.EscapePowerShellString(view.Title);
            const hasHidden: boolean = view.Hidden === true;
            const hasScope: boolean = view.Scope !== null && view.Scope !== undefined;
            const hasAggregations: boolean = view.Aggregations !== null && view.Aggregations !== undefined
                && view.Aggregations.length > 0;
            const hasFormatter: boolean = view.CustomFormatter !== null && view.CustomFormatter !== undefined
                && view.CustomFormatter.length > 0;
            if (hasHidden || hasScope || hasAggregations || hasFormatter) {
                let setParams: string = ` -List "${escapedListTitle}" -Identity "${escapedViewTitle}"`;
                if (hasHidden) { setParams += " -Values @{Hidden=$true}"; }
                if (hasScope) { setParams += ` -Values @{Scope="${view.Scope}"}`; }
                if (hasAggregations) {
                    const aggXml: string = DBGeneratorPowerShell.BuildAggregationsXml(view);
                    setParams += ` -Aggregations "${DBStringUtils.EscapePowerShellString(aggXml)}"`;
                }
                script += `Set-PnPView${setParams}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateViewPostSettings");
            return "";
        }
    };

    public static BuildAggregationsXml = (view: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let xml: string = "";
            const AppendAggregation = (agg: { FieldName: string; Function: string }): void => {
                xml += `<FieldRef Name="${agg.FieldName}" Type="${agg.Function}" />`;
            };
            view.Aggregations.forEach(AppendAggregation);
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.BuildAggregationsXml");
            return "";
        }
    };

    // #endregion

    // #region Completion and removal

    public static GenerateCompletionMessage = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const stats = DBGeneratorPowerShell.CalculateStatistics();
            let script: string = "# ===== COMPLETION =====\n\n";
            script += 'Write-Host ""\n';
            script += 'Write-Host "====================================" -ForegroundColor Green\n';
            script += 'Write-Host "  Script completed successfully!" -ForegroundColor Green\n';
            script += 'Write-Host "====================================" -ForegroundColor Green\n';
            script += `Write-Host "  Lists created: ${stats.listCount}" -ForegroundColor White\n`;
            script += `Write-Host "  Fields created: ${stats.fieldCount}" -ForegroundColor White\n`;
            script += `Write-Host "  Data rows added: ${stats.dataRows}" -ForegroundColor White\n`;
            script += `Write-Host "  Views created: ${stats.viewCount}" -ForegroundColor White\n`;
            script += 'Write-Host "====================================" -ForegroundColor Green\n\n';
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateCompletionMessage");
            return "";
        }
    };

    public static GenerateRemovalFunction = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "# ===== REMOVAL FUNCTION (commented out) =====\n\n";
            script += "<#\n";
            script += "function Remove-AllLists {\n";
            script += '    Write-Host "Removing all lists..." -ForegroundColor Red\n';
            const reversedLists: IListDefinition[] = [...DBState.Lists].reverse();
            const AppendRemoval = (list: IListDefinition): void => {
                const escapedTitle: string = DBStringUtils.EscapePowerShellString(list.Title);
                script += `    Remove-PnPList -Identity "${escapedTitle}" -Force\n`;
                script += `    Write-Host "Removed: ${escapedTitle}" -ForegroundColor Yellow\n`;
            };
            reversedLists.forEach(AppendRemoval);
            script += '    Write-Host "All lists removed." -ForegroundColor Red\n';
            script += "}\n";
            script += "# Remove-AllLists\n";
            script += "#>\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateRemovalFunction");
            return "";
        }
    };

    // #endregion

    // #region Field generation

    public static GenerateFieldPowerShell = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const escapedFieldTitle: string = DBStringUtils.EscapePowerShellString(field.Title);
            let script: string = `# Field: ${field.Title} (${internalName})\n`;
            script += `$existingField = Get-PnPField${listParam} -Identity "${internalName}" -ErrorAction SilentlyContinue\n`;
            script += "if ($null -eq $existingField) {\n";
            script += DBGeneratorPowerShell.GenerateFieldByType(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldSettings(listTitle, internalName, field, siteColumnGroup);
            script += `    Write-Host "  Added field: ${escapedFieldTitle}" -ForegroundColor Gray\n`;
            script += "}\n\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldPowerShell");
            return "";
        }
    };

    public static GenerateFieldByType = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const fieldType: string = field.Format || field.Type;
            switch (fieldType) {
                case "note":
                    return DBGeneratorPowerShell.GenerateNoteField(listTitle, internalName, field, siteColumnGroup);
                case "rich-text":
                    return DBGeneratorPowerShell.GenerateRichTextField(listTitle, internalName, field, siteColumnGroup);
                case "date":
                case "date-time":
                    return DBGeneratorPowerShell.GenerateDateTimeField(listTitle, internalName, field, siteColumnGroup);
                case "currency":
                    return DBGeneratorPowerShell.GenerateCurrencyField(listTitle, internalName, field, siteColumnGroup);
                case "url":
                    return DBGeneratorPowerShell.GenerateUrlField(listTitle, internalName, field, siteColumnGroup);
                case "user":
                    return DBGeneratorPowerShell.GenerateUserField(listTitle, internalName, field, siteColumnGroup);
                case "lookup":
                    return DBGeneratorPowerShell.GenerateLookupField(listTitle, internalName, field, siteColumnGroup);
                case "integer":
                    return DBGeneratorPowerShell.GenerateIntegerField(listTitle, internalName, field, siteColumnGroup);
                case "number":
                    return DBGeneratorPowerShell.GenerateNumberField(listTitle, internalName, field, siteColumnGroup);
                case "boolean":
                    return DBGeneratorPowerShell.GenerateBooleanField(listTitle, internalName, field, siteColumnGroup);
                case "string":
                    return DBGeneratorPowerShell.GenerateStringField(listTitle, internalName, field, siteColumnGroup);
                case "array":
                    return DBGeneratorPowerShell.GenerateArrayField(listTitle, internalName, field, siteColumnGroup);
                case "geolocation":
                    return DBGeneratorPowerShell.GenerateGeolocationField(listTitle, internalName, field, siteColumnGroup);
                default:
                    return DBGeneratorPowerShell.GenerateDefaultTextField(listTitle, internalName, field, siteColumnGroup);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldByType");
            return "";
        }
    };

    public static BuildBaseFieldCmd = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        fieldType: string,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const escapedListTitle: string = DBStringUtils.EscapePowerShellString(listTitle);
            const isSiteColumn: boolean = siteColumnGroup !== null && siteColumnGroup !== undefined;
            let cmd: string = "    Add-PnPField";
            if (!isSiteColumn) {
                cmd += ` -List "${escapedListTitle}"`;
            }
            cmd += ` -InternalName "${internalName}"`;
            cmd += ` -DisplayName "${DBStringUtils.EscapePowerShellString(field.Title)}" -Type ${fieldType}`;
            if (field.Required === true) { cmd += " -Required"; }
            if (isSiteColumn) {
                cmd += ` -Group "${DBStringUtils.EscapePowerShellString(siteColumnGroup)}"`;
            }
            return cmd;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.BuildBaseFieldCmd");
            return "";
        }
    };

    public static BuildListParam = (listTitle: string, siteColumnGroup?: string): string => {
        if (siteColumnGroup !== null && siteColumnGroup !== undefined) { return ""; }
        return ` -List "${DBStringUtils.EscapePowerShellString(listTitle)}"`;
    };

    public static GenerateNoteField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Note", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.NumberOfLines !== null && field.NumberOfLines !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{NumberOfLines=${field.NumberOfLines}}\n`;
            }
            if (field.AppendOnly === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{AppendOnly=$true}\n`;
            }
            if (field.RichText === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{RichText=$true}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateNoteField");
            return "";
        }
    };

    public static GenerateRichTextField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Note", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{RichText=$true; RichTextMode="FullHtml"}\n`;
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateRichTextField");
            return "";
        }
    };

    public static GenerateDateTimeField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "DateTime", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const displayFormat: string | null = field.DisplayFormat || (field.Format === "date" ? "0" : null);
            if (displayFormat !== null) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{DisplayFormat="${displayFormat}"}\n`;
            }
            if (field.FriendlyDisplayFormat) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{FriendlyDisplayFormat="${field.FriendlyDisplayFormat}"}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateDateTimeField");
            return "";
        }
    };

    public static GenerateUrlField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "URL", siteColumnGroup);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateUrlField");
            return "";
        }
    };

    public static GenerateCurrencyField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Currency", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.MinimumValue !== null && field.MinimumValue !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{MinimumValue=${field.MinimumValue}}\n`;
            }
            if (field.MaximumValue !== null && field.MaximumValue !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{MaximumValue=${field.MaximumValue}}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateCurrencyField");
            return "";
        }
    };

    public static GenerateUserField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "User", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.SelectionMode !== null && field.SelectionMode !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{SelectionMode="${field.SelectionMode}"}\n`;
            }
            if (field.SelectionGroup !== null && field.SelectionGroup !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{SelectionGroup=${field.SelectionGroup}}\n`;
            }
            if (field.AllowMultipleValues === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{AllowMultipleValues=$true}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateUserField");
            return "";
        }
    };

    public static GenerateLookupField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const isSiteColumn: boolean = siteColumnGroup !== null && siteColumnGroup !== undefined;
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const lookupList: string = field.LookupList !== null && field.LookupList !== undefined
                ? DBStringUtils.EscapePowerShellString(field.LookupList) : "";
            const lookupField: string = field.LookupField !== null && field.LookupField !== undefined
                ? field.LookupField : "Title";
            let script: string = `    $lookupList = Get-PnPList -Identity "${lookupList}"\n`;
            script += `    Add-PnPField${listParam} -InternalName "${internalName}"`;
            script += ` -DisplayName "${DBStringUtils.EscapePowerShellString(field.Title)}" -Type Lookup`;
            script += ` -AddToDefaultView`;
            if (isSiteColumn) {
                script += ` -Group "${DBStringUtils.EscapePowerShellString(siteColumnGroup)}"`;
            }
            script += "\n";
            script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{LookupList=$lookupList.Id.ToString(); LookupField="${lookupField}"}\n`;
            if (field.AllowMultipleValues === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{AllowMultipleValues=$true}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateLookupField");
            return "";
        }
    };

    public static GenerateIntegerField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Integer", siteColumnGroup);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateIntegerField");
            return "";
        }
    };

    public static GenerateNumberField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Number", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.MinimumValue !== null && field.MinimumValue !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{MinimumValue=${field.MinimumValue}}\n`;
            }
            if (field.MaximumValue !== null && field.MaximumValue !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{MaximumValue=${field.MaximumValue}}\n`;
            }
            if (field.ShowAsPercentage === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{ShowAsPercentage=$true}\n`;
            }
            if (field.DisplayFormat !== null && field.DisplayFormat !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{DisplayFormat="${field.DisplayFormat}"}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateNumberField");
            return "";
        }
    };

    public static GenerateBooleanField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Boolean", siteColumnGroup);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateBooleanField");
            return "";
        }
    };

    public static GenerateStringField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                return DBGeneratorPowerShell.GenerateChoiceField(listTitle, internalName, field, siteColumnGroup);
            }
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Text", siteColumnGroup);
            script += "\n";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.MaxLength !== null && field.MaxLength !== undefined) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{MaxLength=${field.MaxLength}}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateStringField");
            return "";
        }
    };

    public static GenerateChoiceField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const isSiteColumn: boolean = siteColumnGroup !== null && siteColumnGroup !== undefined;
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const choices: string = DBGeneratorPowerShell.BuildChoicesString(field.Enum);
            let script: string = `    Add-PnPField${listParam} -InternalName "${internalName}"`;
            script += ` -DisplayName "${DBStringUtils.EscapePowerShellString(field.Title)}" -Type Choice`;
            script += ` -Choices ${choices}`;
            if (field.Required === true) { script += " -Required"; }
            if (isSiteColumn) {
                script += ` -Group "${DBStringUtils.EscapePowerShellString(siteColumnGroup)}"`;
            }
            script += "\n";
            if (field.FillInChoice === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{FillInChoice=$true}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateChoiceField");
            return "";
        }
    };

    public static GenerateArrayField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Items !== null && field.Items !== undefined
                && field.Items.Enum !== null && field.Items.Enum !== undefined && field.Items.Enum.length > 0) {
                return DBGeneratorPowerShell.GenerateMultiChoiceField(listTitle, internalName, field, siteColumnGroup);
            }
            return DBGeneratorPowerShell.GenerateDefaultTextField(listTitle, internalName, field, siteColumnGroup);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateArrayField");
            return "";
        }
    };

    public static GenerateMultiChoiceField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const isSiteColumn: boolean = siteColumnGroup !== null && siteColumnGroup !== undefined;
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const choices: string = DBGeneratorPowerShell.BuildChoicesString(field.Items.Enum);
            let script: string = `    Add-PnPField${listParam} -InternalName "${internalName}"`;
            script += ` -DisplayName "${DBStringUtils.EscapePowerShellString(field.Title)}" -Type MultiChoice`;
            script += ` -Choices ${choices}`;
            if (field.Required === true) { script += " -Required"; }
            if (isSiteColumn) {
                script += ` -Group "${DBStringUtils.EscapePowerShellString(siteColumnGroup)}"`;
            }
            script += "\n";
            if (field.FillInChoice === true) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{FillInChoice=$true}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateMultiChoiceField");
            return "";
        }
    };

    public static GenerateGeolocationField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Geolocation", siteColumnGroup);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateGeolocationField");
            return "";
        }
    };

    public static GenerateDefaultTextField = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = DBGeneratorPowerShell.BuildBaseFieldCmd(listTitle, internalName, field, "Text", siteColumnGroup);
            script += "\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateDefaultTextField");
            return "";
        }
    };

    public static BuildChoicesString = (choices: string[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let result: string = "";
            const AppendChoice = (choice: string): void => {
                if (result.length > 0) { result += ","; }
                result += '"' + DBStringUtils.EscapePowerShellString(choice) + '"';
            };
            choices.forEach(AppendChoice);
            return result;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.BuildChoicesString");
            return "";
        }
    };

    // #endregion

    // #region Field settings (post-creation)

    public static GenerateFieldSettings = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            script += DBGeneratorPowerShell.GenerateFieldDefaultValue(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldValidation(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldFormVisibility(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldCustomFormatter(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldDescription(listTitle, internalName, field, siteColumnGroup);
            script += DBGeneratorPowerShell.GenerateFieldEnforceUnique(listTitle, internalName, field, siteColumnGroup);
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldSettings");
            return "";
        }
    };

    public static GenerateFieldDefaultValue = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.DefaultValue === null || field.DefaultValue === undefined) { return ""; }
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const escapedValue: string = DBStringUtils.EscapePowerShellString(field.DefaultValue);
            return `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{DefaultValue="${escapedValue}"}\n`;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldDefaultValue");
            return "";
        }
    };

    public static GenerateFieldValidation = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.ValidationFormula === null || field.ValidationFormula === undefined) { return ""; }
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const escapedFormula: string = DBStringUtils.EscapePowerShellString(field.ValidationFormula);
            let script: string = `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{ValidationFormula="${escapedFormula}"`;
            if (field.ValidationMessage !== null && field.ValidationMessage !== undefined) {
                const escapedMessage: string = DBStringUtils.EscapePowerShellString(field.ValidationMessage);
                script += `; ValidationMessage="${escapedMessage}"`;
            }
            script += "}\n";
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldValidation");
            return "";
        }
    };

    public static GenerateFieldFormVisibility = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let script: string = "";
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            if (field.ShowInDisplayForm === false) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{ShowInDisplayForm=$false}\n`;
            }
            if (field.ShowInEditForm === false) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{ShowInEditForm=$false}\n`;
            }
            if (field.ShowInNewForm === false) {
                script += `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{ShowInNewForm=$false}\n`;
            }
            return script;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldFormVisibility");
            return "";
        }
    };

    public static GenerateFieldCustomFormatter = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.CustomFormatter === null || field.CustomFormatter === undefined) { return ""; }
            if (field.CustomFormatter.length === 0) { return ""; }
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const escapedFormatter: string = DBStringUtils.EscapePowerShellString(field.CustomFormatter);
            return `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{CustomFormatter="${escapedFormatter}"}\n`;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldCustomFormatter");
            return "";
        }
    };

    public static GenerateFieldDescription = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Description === null || field.Description === undefined) { return ""; }
            if (field.Description.length === 0) { return ""; }
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            const escapedDesc: string = DBStringUtils.EscapePowerShellString(field.Description);
            return `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{Description="${escapedDesc}"}\n`;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldDescription");
            return "";
        }
    };

    public static GenerateFieldEnforceUnique = (
        listTitle: string,
        internalName: string,
        field: IFieldDefinition,
        siteColumnGroup?: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.EnforceUniqueValues !== true) { return ""; }
            const listParam: string = DBGeneratorPowerShell.BuildListParam(listTitle, siteColumnGroup);
            return `    Set-PnPField${listParam} -Identity "${internalName}" -Values @{EnforceUniqueValues=$true; Indexed=$true}\n`;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorPowerShell.GenerateFieldEnforceUnique");
            return "";
        }
    };

    // #endregion
}
